import { PLANS, money, planById } from "./constants.js";
import {
  listAccounts,
  listAdjustments,
  listInvoices,
  listOrganizations,
  listPayments,
  listSubscriptions,
  seatsForOrg,
  subscriptionFor,
} from "./store.js";

function monthKey(iso) {
  return String(iso || "").slice(0, 7);
}

function thisMonth() {
  return new Date().toISOString().slice(0, 7);
}

function monthsBack(n) {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - n);
  return date.toISOString().slice(0, 7);
}

function inLastDays(iso, days) {
  const t = Date.parse(iso);
  return !Number.isNaN(t) && Date.now() - t <= days * 86400000;
}

export function buildRevenueReport() {
  const paid = listPayments().filter((row) => row.result === "succeeded");
  const month = thisMonth();
  const monthlyPaid = paid.filter((row) => monthKey(row.at) === month);
  const mrr = listSubscriptions()
    .filter((row) => row.status === "active" || row.status === "grace" || row.status === "past_due")
    .reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const byPlan = PLANS.map((plan) => {
    const subs = listSubscriptions().filter((row) => row.plan_id === plan.id && row.status !== "cancelled");
    return {
      id: plan.id,
      label: plan.label,
      seats: subs.length,
      mrr: subs.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    };
  }).filter((row) => row.seats || row.mrr);
  const byOrg = listOrganizations().map((org) => {
    const sub = subscriptionFor(org.id);
    return {
      id: org.id,
      name: org.name,
      mrr: Number(sub?.amount || 0),
      collected: paid.filter((row) => row.owner_id === org.id).reduce((sum, row) => sum + Number(row.amount || 0), 0),
    };
  });
  return {
    mrr,
    arr: mrr * 12,
    collectedThisMonth: monthlyPaid.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    collectedAll: paid.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    byPlan,
    byOrg,
  };
}

export function buildUserBaseReport() {
  const accounts = listAccounts();
  const active = accounts.filter((row) => row.status === "active" || row.status === "beta");
  const beta = accounts.filter((row) => row.status === "beta");
  const paid = accounts.filter((row) => {
    const sub = subscriptionFor(row.id) || subscriptionFor(row.organization_id);
    return row.status === "active" && Number(sub?.amount || 0) > 0;
  });
  const cancelled90 = accounts.filter((row) => row.status === "cancelled" && inLastDays(row.created_at, 90));
  const new90 = accounts.filter((row) => inLastDays(row.created_at, 90));
  const startPool = Math.max(1, accounts.length - new90.length + cancelled90.length);
  const churn = cancelled90.length / startPool;
  const growth = new90.length / startPool;
  return {
    total: accounts.length,
    active: active.length,
    beta: beta.length,
    paid: paid.length,
    suspended: accounts.filter((row) => row.status === "suspended").length,
    cancelled: accounts.filter((row) => row.status === "cancelled").length,
    churnRate: churn,
    growthRate: growth,
    byRole: ["agent", "broker", "admin", "corporate"].map((role) => ({
      role,
      count: accounts.filter((row) => row.role === role).length,
    })),
  };
}

export function buildTaxReport() {
  const paid = listPayments().filter((row) => row.result === "succeeded");
  const credits = listAdjustments();
  const byMonth = {};
  paid.forEach((row) => {
    const key = monthKey(row.at);
    byMonth[key] = byMonth[key] || { period: key, gross: 0, refunds: 0 };
    byMonth[key].gross += Number(row.amount || 0);
  });
  listPayments()
    .filter((row) => row.result === "refunded")
    .forEach((row) => {
      const key = monthKey(row.refunded_at || row.at);
      byMonth[key] = byMonth[key] || { period: key, gross: 0, refunds: 0 };
      byMonth[key].refunds += Number(row.amount || 0);
    });
  const byJurisdiction = {};
  listInvoices()
    .filter((row) => row.status === "paid")
    .forEach((row) => {
      const key = row.tax_jurisdiction || "Untagged";
      byJurisdiction[key] = (byJurisdiction[key] || 0) + Number(row.amount || 0);
    });
  return {
    periods: Object.values(byMonth).sort((a, b) => a.period.localeCompare(b.period)),
    byJurisdiction: Object.entries(byJurisdiction).map(([jurisdiction, amount]) => ({ jurisdiction, amount })),
    credits: credits.reduce((sum, row) => sum + Number(row.amount || 0), 0),
  };
}

export function buildCorporateReport() {
  return listOrganizations().map((org) => {
    const seats = seatsForOrg(org.id);
    const sub = subscriptionFor(org.id);
    const history = listPayments().filter((row) => row.owner_id === org.id);
    return {
      org,
      seats,
      plan: planById(sub?.plan_id),
      subscription: sub,
      collected: history.filter((row) => row.result === "succeeded").reduce((sum, row) => sum + Number(row.amount || 0), 0),
      attempts: history.length,
    };
  });
}

export function buildForecast() {
  const accounts = listAccounts();
  const months = [2, 1, 0].map((n) => monthsBack(n));
  const series = months.map((period) => {
    const created = accounts.filter((row) => monthKey(row.created_at) === period).length;
    const cancelled = accounts.filter((row) => row.status === "cancelled" && monthKey(row.created_at) === period).length;
    const active = accounts.filter((row) => monthKey(row.created_at) <= period && row.status !== "cancelled").length;
    return { period, created, cancelled, active, net: created - cancelled };
  });
  const avgNet = series.reduce((sum, row) => sum + row.net, 0) / Math.max(1, series.length);
  const lastActive = series[series.length - 1]?.active || accounts.filter((row) => row.status !== "cancelled").length;
  const projections = [30, 60, 90].map((days, index) => ({
    days,
    accounts: Math.max(0, Math.round(lastActive + avgNet * (index + 1))),
  }));
  const segments = {
    role: ["agent", "broker", "admin", "corporate"].map((role) => ({
      id: role,
      count: accounts.filter((row) => row.role === role).length,
    })),
    state: rollup(accounts, "state"),
    county: rollup(accounts, "county"),
    city: rollup(accounts, "city"),
  };
  return { series, avgNet, projections, segments };
}

function rollup(rows, key) {
  const map = {};
  rows.forEach((row) => {
    const label = row[key] || "Untagged";
    map[label] = (map[label] || 0) + 1;
  });
  return Object.entries(map)
    .map(([id, count]) => ({ id, count }))
    .sort((a, b) => b.count - a.count);
}

export function reportLines() {
  const revenue = buildRevenueReport();
  const users = buildUserBaseReport();
  const tax = buildTaxReport();
  return [
    ["MRR", money(revenue.mrr)],
    ["ARR", money(revenue.arr)],
    ["Collected this month", money(revenue.collectedThisMonth)],
    ["Active accounts", String(users.active)],
    ["Beta", String(users.beta)],
    ["Paid", String(users.paid)],
    ["Churn (90d)", `${Math.round(users.churnRate * 100)}%`],
    ["Growth (90d)", `${Math.round(users.growthRate * 100)}%`],
    ...tax.byJurisdiction.map((row) => [`Taxable · ${row.jurisdiction}`, money(row.amount)]),
  ];
}
